import { PHASES_API_URL } from '@/Config/ApiConfig';
import { useImgPreloader } from '@/hooks/useImagePreload';
import { PhaseIcons } from '@/pages/PhasePage/Icons';
import { PhaseTooltip } from '@/pages/PhasePage/PhaseTooltip';
import { questsActivitiesStore } from '@/pages/quests/QuestsActivities/store';
import { questsAdventuresStore } from '@/pages/quests/QuestsAdventures/store';
import { questsPhasesStore } from '@/pages/quests/QuestsPhases/store';
import { phaseService } from '@/services/phaseApi';
import { retroPointsService } from '@/services/retroPoints';
import { PhaseTask } from '@/types/phases';
import clsx from 'clsx';
import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { useAccount as useEVMAccount } from 'wagmi';
import './styles.css';

interface TaskItemProps {
  task: PhaseTask;
  journeyId?: number;
  variant: 'phase' | 'adventure' | 'activity';
}

export function TaskItem({ task, journeyId, variant }: TaskItemProps) {
  const { isConnected, address } = useEVMAccount();
  const isCompleted = task.isCompleted && isConnected;
  const [showTimer, setShowTimer] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isSuccess, setIsSuccess] = useState<boolean | null>(null);

  const { imgsPreloaded } = useImgPreloader(
    task.images?.map(el => PHASES_API_URL + el) ?? []
  );

  useEffect(() => {
    if (!showTimer) return;

    const timer = setInterval(() => {
      setCountdown(prev => prev - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [showTimer]);

  useEffect(() => {
    if (countdown <= 0) {
      reset();
    }
  }, [countdown]);

  useEffect(() => {
    if (!isConnected) {
      reset();
    }
  }, [isConnected]);

  const reset = () => {
    setShowTimer(false);
    setIsSuccess(null);
  };

  const handleRefresh = async (e: MouseEvent) => {
    e.preventDefault();
    if (showTimer || !address) return;
    setShowTimer(true);
    setCountdown(9);

    if (variant === 'phase' || variant === 'adventure') {
      const isCompleted = await phaseService.getPhaseTaskStatus(
        task.id,
        address,
        journeyId
      );

      if (isCompleted !== null && variant === 'phase') {
        const { journeys, setTaskStatus } = questsPhasesStore.getState();
        const phase = journeys.find(j => j.id === journeyId)?.phase;

        if (phase) {
          setTaskStatus(phase, task.id, isCompleted);
        }
      } else if (isCompleted !== null && variant === 'adventure' && journeyId) {
        const { setTaskStatus } = questsAdventuresStore.getState();
        setTaskStatus(journeyId, task.id, isCompleted);
      }
    } else if (variant === 'activity') {
      const isCompleted = await retroPointsService.refreshQuestTaskById(
        task.id
      );

      if (isCompleted !== null) {
        const { setTaskStatus } = questsActivitiesStore.getState();
        setTaskStatus(task.id, isCompleted);
      }
    }
    setIsSuccess(isCompleted ?? false);
  };

  const tooltipContent = useMemo(() => {
    if (isSuccess !== null && !isSuccess) {
      return {
        title: (
          <>
            <PhaseIcons.Error />
            Quest is not completed yet
          </>
        ),
        description:
          'Please try again later or contact support of your task is completed',
      };
    }

    return {
      title: 'Refresh to check the status',
      description: 'Updates are available every 9 seconds',
    };
  }, [isSuccess]);

  const title = useMemo(() => {
    let i = 0;

    return task.title.replaceAll('image', () => {
      const img = `<img src="${PHASES_API_URL}${
        task.images?.[i] ?? ''
      }" alt="image${i}" />`;
      i++;
      return img;
    });
  }, [task.title, task.images]);

  return (
    <div className="quests-tasks--item">
      <div className="quests-tasks--item-left-icon">
        {!isCompleted && <PhaseIcons.Process width={24} height={24} />}
        {isCompleted && <PhaseIcons.Done width={24} height={24} />}
      </div>
      <span
        className={clsx(
          'quests-tasks--item-content',
          imgsPreloaded && 'loaded'
        )}
        dangerouslySetInnerHTML={{ __html: title }}
      />
      {}
      <div className="ms-auto">
        {!task.isCompleted && isConnected && (
          <PhaseTooltip {...tooltipContent}>
            <span
              className="quests-tasks--item-refresh"
              onClick={handleRefresh}
            >
              {showTimer && <>{countdown}s</>}
              {!showTimer && <PhaseIcons.Refresh width={14} height={14} />}
            </span>
          </PhaseTooltip>
        )}
      </div>
    </div>
  );
}
